// Empfängerfeld mit Vorschlägen (Phase 2.A): bisherige Empfänger aus dem Mailverlauf, Kontakte und
// Einrichtungen aus dem CRM, und – solange das Feld leer ist – die fünf Kontakte mit der jüngsten
// CRM-Aktivität plus die fünf zuletzt angeschriebenen Adressen.
import { useEffect, useRef, useState } from 'react';
import { api, type Adresse } from '../lib/api.ts';

const fmt = (a: Adresse) => (a.name ? `${a.name} <${a.address}>` : a.address);

export function AdressFeld({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [liste, setListe] = useState<Array<{ titel: string; items: Adresse[] }>>([]);
  const [sel, setSel] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  // Was gerade getippt wird: der Teil nach dem letzten Komma
  const teile = value.split(/[,;]/);
  const aktuell = (teile[teile.length - 1] ?? '').trim();

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (aktuell.length < 2) {
        void api
          .adressenZuletzt()
          .then((r) =>
            setListe(
              [
                { titel: 'Zuletzt im CRM', items: r.crm },
                { titel: 'Zuletzt angeschrieben', items: r.verlauf },
              ].filter((g) => g.items.length),
            ),
          )
          .catch(() => setListe([]));
      } else {
        void api
          .adressen(aktuell)
          .then((r) => {
            const crm = r.adressen.filter((a) => a.quelle !== 'verlauf');
            const verlauf = r.adressen.filter((a) => a.quelle === 'verlauf');
            setListe(
              [
                { titel: 'CRM', items: crm },
                { titel: 'Mailverlauf', items: verlauf },
              ].filter((g) => g.items.length),
            );
          })
          .catch(() => setListe([]));
      }
      setSel(0);
    }, 200);
  }, [aktuell, open]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const flach = liste.flatMap((g) => g.items);
  const waehlen = (a: Adresse) => {
    const vorne = teile
      .slice(0, -1)
      .map((t) => t.trim())
      .filter(Boolean);
    onChange([...vorne, fmt(a)].join(', ') + ', ');
    setOpen(false);
  };

  return (
    <div className="adr-wrap" ref={wrap}>
      <input
        aria-label={ariaLabel}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || !flach.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSel((s) => Math.min(s + 1, flach.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSel((s) => Math.max(s - 1, 0));
          } else if (e.key === 'Enter' && flach[sel]) {
            e.preventDefault();
            waehlen(flach[sel]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
        autoComplete="off"
      />
      {open && liste.length > 0 && (
        <div className="adr-pop" role="listbox">
          {liste.map((g) => (
            <div key={g.titel}>
              <div className="adr-h">{g.titel}</div>
              {g.items.map((a) => {
                const i = flach.indexOf(a);
                return (
                  <button
                    key={a.quelle + a.address}
                    type="button"
                    role="option"
                    aria-selected={i === sel}
                    className={`adr-item${i === sel ? ' on' : ''}`}
                    onMouseEnter={() => setSel(i)}
                    onClick={() => waehlen(a)}
                  >
                    <span className="adr-name">{a.name || a.address}</span>
                    <span className="adr-mail">{a.name ? a.address : ''}</span>
                    <span className="adr-info">
                      {[a.einrichtung, a.rolle, a.hinweis].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
